import { useCallback, useState } from "react";
import { motion } from "framer-motion";
import { LOGO_URL } from "@/lib/api";
import { PreorderForm } from "@/components/PreorderForm";
import { DiceGame } from "@/components/DiceGame";
import { SharePanel } from "@/components/SharePanel";
import { Footer } from "@/components/Footer";
import { Sparkle } from "@/components/Sparkle";

const EASE = [0.16, 1, 0.3, 1];

const SPARKLES = [
  { top: "10%", left: "10%", size: 26, delay: "0s", color: "text-[#2E5BFF]" },
  { top: "16%", right: "12%", size: 18, delay: "0.9s", color: "text-white" },
  { top: "44%", left: "7%", size: 14, delay: "1.6s", color: "text-white" },
  { top: "38%", right: "8%", size: 22, delay: "0.4s", color: "text-[#2E5BFF]" },
  { top: "6%", left: "48%", size: 12, delay: "2.1s", color: "text-[#7FB2FF]" },
];

const readLS = (k, fallback) => {
  try {
    const v = localStorage.getItem(k);
    return v === null ? fallback : v;
  } catch {
    return fallback;
  }
};

export default function Home() {
  const [unlocked, setUnlocked] = useState(() => readLS("juju_preordered", "0") === "1");
  const [rollsLeft, setRollsLeft] = useState(() => {
    const v = parseInt(readLS("juju_rolls_left", ""), 10);
    return Number.isFinite(v) ? Math.max(0, v) : 1;
  });

  const persistRolls = (n) => {
    try {
      localStorage.setItem("juju_rolls_left", String(n));
    } catch {}
  };

  const consumeRoll = useCallback(() => {
    setRollsLeft((r) => {
      const n = Math.max(0, r - 1);
      persistRolls(n);
      return n;
    });
  }, []);

  const addRolls = useCallback((n) => {
    setRollsLeft((r) => {
      const v = r + n;
      persistRolls(v);
      return v;
    });
  }, []);

  const handleSubmitted = useCallback(() => {
    try {
      localStorage.setItem("juju_preordered", "1");
    } catch {}
    setUnlocked(true);
    addRolls(3);
  }, [addRolls]);

  return (
    <div className="grain min-h-screen">
      {/* soft ambient light */}
      <div className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute left-1/2 top-[-15%] h-[55vh] w-[80vw] -translate-x-1/2 rounded-full bg-white/50 blur-[130px]" />
        <div className="absolute bottom-[-20%] right-[-10%] h-[45vh] w-[45vw] rounded-full bg-[#7FB2FF]/30 blur-[150px]" />
      </div>

      <section data-testid="hero-banner" className="relative flex min-h-screen flex-col px-5 py-14">
        <div className="m-auto flex w-full flex-col items-center">
          {SPARKLES.map((s, i) => (
            <Sparkle
              key={i}
              className={`sparkle pointer-events-none absolute ${s.color}`}
              style={{
                top: s.top,
                left: s.left,
                right: s.right,
                width: s.size,
                height: s.size,
                animationDelay: s.delay,
              }}
            />
          ))}

          <motion.div
            data-testid="good-juju-main-banner-logo"
            initial={{ opacity: 0, scale: 0.9, filter: "blur(14px)" }}
            animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
            transition={{ duration: 1.1, ease: EASE }}
          >
            <img
              src={LOGO_URL}
              alt="Good Juju — liquid chrome logo"
              className="w-[min(82vw,600px)] mix-blend-multiply"
              draggable="false"
            />
          </motion.div>

          <motion.div
            data-testid="dice-wrapper"
            initial={{ opacity: 0, scale: 0.8, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.6 }}
          >
            <DiceGame rollsLeft={rollsLeft} consumeRoll={consumeRoll} unlocked={unlocked} />
          </motion.div>

          <div className="mt-4 w-full">
            <PreorderForm onSubmitted={handleSubmitted} />
          </div>

          {unlocked && <SharePanel onShare={addRolls} />}
        </div>
      </section>

      <Footer />
    </div>
  );
}
